import { Injectable, inject } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { SessionService } from '@seech/auth-ng';
import { NavItem } from '@seech-sub/layout-ng';
import { Observable, map } from 'rxjs';
import {
  OperationsAccessibleRouteOrder,
  OperationsPermissions,
  OperationsRoles,
  OperationsRoutePermissions,
  OperationsRoutes,
} from '../../constants/operations.constants';
import {
  OperationsSessionResponse,
  OperationsUserSessionResponse,
} from '../../models';

export interface SecuredNavItem extends NavItem {
  permissions: readonly string[];
}

@Injectable({ providedIn: 'root' })
export class OperationsAccessService {
  private readonly router = inject(Router);
  private readonly sessionService = inject(
    SessionService<OperationsUserSessionResponse, OperationsSessionResponse>,
  );

  getCurrentUser(): Observable<OperationsUserSessionResponse | null> {
    return this.sessionService
      .getCurrentUserAsync()
      .pipe(map((user) => user ?? null));
  }

  canAccessModule(
    user: OperationsUserSessionResponse | null | undefined,
    permissions: readonly string[],
  ): boolean {
    if (!permissions.length) {
      return true;
    }

    const grantedPermissions = this.getGrantedPermissions(user);
    return (
      grantedPermissions.has(OperationsPermissions.access) &&
      permissions.some((permission) => grantedPermissions.has(permission))
    );
  }

  canAccessRoute(
    user: OperationsUserSessionResponse | null | undefined,
    route: string,
  ): boolean {
    const permissions = OperationsRoutePermissions[route] ?? [];
    return this.canAccessModule(user, permissions);
  }

  hasPermission(
    user: OperationsUserSessionResponse | null | undefined,
    permission: string,
  ): boolean {
    return this.getGrantedPermissions(user).has(permission);
  }

  filterNavGroups(
    groups: readonly (readonly SecuredNavItem[])[],
    user: OperationsUserSessionResponse | null | undefined,
  ): NavItem[][] {
    return groups
      .map((group) =>
        group
          .filter((item) => this.canAccessModule(user, item.permissions))
          .map((item) => this.toNavItem(item)),
      )
      .filter((group) => group.length > 0);
  }

  firstAccessibleRoute(
    user: OperationsUserSessionResponse | null | undefined,
  ): string | null {
    return (
      OperationsAccessibleRouteOrder.find((route) =>
        this.canAccessRoute(user, route),
      ) ?? null
    );
  }

  /**
   * Where to send an account that cannot open the route it asked for.
   *
   * If it can open something else, it goes there. If it can open nothing at
   * all, it gets told: this used to fall through to `logout`, which signed the
   * account out mid-navigation and left it staring at a login screen with no
   * statement of what happened or what to do about it.
   */
  redirectToFirstAccessibleRoute(
    user: OperationsUserSessionResponse | null | undefined,
  ): UrlTree {
    const route = this.firstAccessibleRoute(user);
    return this.router.createUrlTree([
      '/' + (route ?? OperationsRoutes.noAccess),
    ]);
  }

  private getGrantedPermissions(
    user: OperationsUserSessionResponse | null | undefined,
  ): Set<string> {
    const permissions = new Set(
      (user?.permissions ?? '')
        .split(/[,;\r\n]+/)
        .map((permission) => permission.trim())
        .filter(Boolean),
    );

    if (this.hasOwnerRole(user)) {
      Object.values(OperationsPermissions).forEach((permission) =>
        permissions.add(permission),
      );
    }

    return permissions;
  }

  private hasOwnerRole(
    user: OperationsUserSessionResponse | null | undefined,
  ): boolean {
    return (user?.roles ?? '')
      .split(/[,;\r\n]+/)
      .some(
        (role) =>
          role.trim().toLowerCase() === OperationsRoles.owner.toLowerCase(),
      );
  }

  private toNavItem(item: SecuredNavItem): NavItem {
    return {
      label: item.label,
      link: item.link,
      isHidden: item.isHidden,
      icon: item.icon,
      action: item.action,
      external: item.external,
    };
  }
}
